import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import MenuItem from "@mui/material/MenuItem";
import { Link, NavLink, useNavigate } from "react-router-dom";
import logo from "./logo.png";
import { AccountMenuContainerForBigScreen } from "./styles";
import { AccountMenuContainerForSmallScreen } from "./styles";
import { SearchBarAfterMenu, SearchBarBeforeMenu } from "./styles";
import { responsiveAppBarStyles } from "./styles";
import { LoggedInAppBarProps, pageSteps } from "./types";
import { restrictedPathsForSearchBar } from "./types";
import { GlobalSearchBar } from "./GlobalSearchBar";
import { lazy, Suspense, useEffect, useState } from "react";
import { AccountMenu } from "./AccountMenu";
import { Button } from "@mui/material";
import { SendInviteModal } from "../Modal/SendInviteModal";
import { InviteSvg } from "../SVGIcons";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import {
  ENTERPRISE_IMAGE_PATH,
  SVG_ICON_COLOR,
  isEnterprise,
} from "../../theme";

const SupportModal = lazy(() =>
  import("../Modal/SupportModal").then((m) => ({ default: m.SupportModal }))
);

export const LoggedInAppBar: React.FC<LoggedInAppBarProps> = ({
  currentUser,
  userPages,
  handleOpenNavMenu,
  anchorElNav,
  handleCloseNavMenu,
  handleOpenUserMenu,
  anchorElUser,
  user,
  userMenu,
  handleCloseUserMenu,
  setAnchorElUser,
}) => {
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [allowDisplaySearchBar, setAllowDisplaySearchBar] = useState(false);
  const [isSendInviteModalOpen, setIsSendInviteModalOpen] =
    useState<boolean>(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState<boolean>(false);
  const [showSupportHint, setShowSupportHint] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && !localStorage.getItem("support_hint_seen")) {
      setShowSupportHint(true);
      const t = setTimeout(() => setShowSupportHint(false), 8000);
      return () => clearTimeout(t);
    }
  }, [currentUser]);

  useEffect(() => {
    const url = window.location.href;
    const shouldHideSearch = restrictedPathsForSearchBar.some(
      (path) => url && url.includes(path)
    );
    setShowSearchBar(!shouldHideSearch);
  }, [showSearchBar]);

  const handleInviteFriends = async () => {
    setIsSendInviteModalOpen(true);
    handleCloseNavMenu();
  };

  return (
    <>
      {currentUser && (
        <SendInviteModal
          isSendInviteModalOpen={isSendInviteModalOpen}
          setIsSendInviteModalOpen={setIsSendInviteModalOpen}
          userId={currentUser.getUserId()!}
        />
      )}
      {currentUser && (
        <Suspense fallback={null}>
          <SupportModal
            isSupportModalOpen={isSupportModalOpen}
            setIsSupportModalOpen={setIsSupportModalOpen}
            userId={currentUser.getUserId()!}
          />
        </Suspense>
      )}
      <AppBar
        sx={{ ...responsiveAppBarStyles.appBar, ...{ zIndex: 2115 } }}
        position="static"
      >
        <Container maxWidth="xl">
          <Toolbar>
            {/* icon on medium display */}
            <Box sx={responsiveAppBarStyles.mediumDisplayImageLogoContainer}>
              <Link to="/my-tracks">
                <img
                  style={{ width: "150px" }}
                  className="logo-image"
                  src={isEnterprise ? ENTERPRISE_IMAGE_PATH : logo}
                  alt="logo"
                />
              </Link>
            </Box>
            <SearchBarBeforeMenu>
              {showSearchBar && (
                <Box sx={responsiveAppBarStyles.globalSearchBarContainer}>
                  {currentUser?.getUserId() && (
                    <GlobalSearchBar
                      userId={currentUser?.getUserId()}
                      allowDisplaySearchBar={allowDisplaySearchBar}
                      setAllowDisplaySearchBar={setAllowDisplaySearchBar}
                    />
                  )}
                </Box>
              )}
            </SearchBarBeforeMenu>
            {/* user pages on medium display */}
            <Box sx={responsiveAppBarStyles.mediumDisplayUserPagesContainer}>
              {currentUser &&
                userPages.map((userPage: any) => (
                  <NavLink
                    id={pageSteps[userPage.name] || ""}
                    style={({ isActive }) =>
                      userPage.name !== "Mint Song" && isActive
                        ? responsiveAppBarStyles.userPageMintSong
                        : userPage.name === "Mint Song" && isActive
                        ? responsiveAppBarStyles.userPageMintSongAndActive
                        : responsiveAppBarStyles.userPageNotMintSong
                    }
                    key={userPage.name}
                    to={userPage.route}
                  >
                    {userPage.name !== "Mint Song" && userPage.name}
                    {userPage.name === "Mint Song" && (
                      <Box sx={responsiveAppBarStyles.userPagesContainer}>
                        {userPage.name}
                      </Box>
                    )}
                  </NavLink>
                ))}
            </Box>
            {/* userPages menu on xsmall display */}
            {currentUser && (
              <Box sx={{ display: "flex", alignContent: "center" }}>
                {!allowDisplaySearchBar && (
                  <Box sx={responsiveAppBarStyles.menuIconsButtonContainer}>
                    <IconButton
                      size="large"
                      aria-label="account of current user"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      onClick={handleOpenNavMenu}
                      color="inherit"
                      disableFocusRipple
                      disableRipple
                    >
                      <MenuIcon
                        id="small-screen-menu"
                        sx={{ marginLeft: "-20px" }}
                      />
                    </IconButton>
                    <Menu
                      id="menu-appbar"
                      anchorEl={anchorElNav}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      keepMounted
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      open={Boolean(anchorElNav)}
                      onClose={handleCloseNavMenu}
                      sx={responsiveAppBarStyles.menu}
                    >
                      {userPages.map((userPage: any) =>
                        userPage.name === "Mint Song" ? (
                          <span key={userPage.name} />
                        ) : (
                          <MenuItem
                            key={userPage.name}
                            sx={responsiveAppBarStyles.mediumScreenMenuIcon}
                            onClick={() => {
                              navigate(userPage.route);
                              handleCloseNavMenu();
                            }}
                          >
                            {userPage.component}
                            &nbsp;&nbsp;
                            {userPage.name}
                          </MenuItem>
                        )
                      )}
                      {!isEnterprise && (
                        <MenuItem
                          sx={responsiveAppBarStyles.mediumScreenMenuIcon}
                          onClick={handleInviteFriends}
                        >
                          <InviteSvg color={SVG_ICON_COLOR} />
                          &nbsp;&nbsp;Invite Friends
                        </MenuItem>
                      )}
                      <MenuItem
                        sx={responsiveAppBarStyles.mediumScreenMenuIcon}
                        onClick={() => { setIsSupportModalOpen(true); handleCloseNavMenu(); }}
                      >
                        <HelpOutlineIcon fontSize="small" />
                        &nbsp;&nbsp;Contact Support
                      </MenuItem>
                    </Menu>
                  </Box>
                )}
                <Box
                  id="small-screen-search"
                  sx={responsiveAppBarStyles.searchBarAfterMenuContainer}
                >
                  <SearchBarAfterMenu>
                    {showSearchBar && (
                      <Box sx={responsiveAppBarStyles.globalSearchBarContainer}>
                        {currentUser?.getUserId() && (
                          <GlobalSearchBar
                            userId={currentUser?.getUserId()}
                            allowDisplaySearchBar={allowDisplaySearchBar}
                            setAllowDisplaySearchBar={setAllowDisplaySearchBar}
                          />
                        )}
                      </Box>
                    )}
                  </SearchBarAfterMenu>
                </Box>
              </Box>
            )}
            {!allowDisplaySearchBar && (
              <Box sx={responsiveAppBarStyles.imageLogoContainer}>
                <Link to="/my-tracks">
                  <img
                    className="logo-image"
                    src={isEnterprise ? ENTERPRISE_IMAGE_PATH : logo}
                    alt="logo"
                  />
                </Link>
              </Box>
            )}
            {!allowDisplaySearchBar && (
              <AccountMenuContainerForSmallScreen>
                <AccountMenu
                  handleOpenUserMenu={handleOpenUserMenu}
                  user={user}
                  currentUser={currentUser}
                  anchorElUser={anchorElUser}
                  userMenu={userMenu}
                  setAnchorElUser={setAnchorElUser}
                  handleCloseUserMenu={handleCloseUserMenu}
                />
              </AccountMenuContainerForSmallScreen>
            )}
            {!isEnterprise && (
              <AccountMenuContainerForBigScreen>
                <Button
                  onClick={handleInviteFriends}
                  sx={responsiveAppBarStyles.inviteFriendsButton}
                  id="step-8"
                >
                  Invite Friends
                </Button>
              </AccountMenuContainerForBigScreen>
            )}
            <AccountMenuContainerForBigScreen>
              <Tooltip
                title="New! Contact Support — let us know if you need help"
                open={showSupportHint}
                placement="bottom"
                arrow
              >
                <IconButton
                  color="inherit"
                  onClick={() => {
                    setIsSupportModalOpen(true);
                    setShowSupportHint(false);
                    localStorage.setItem("support_hint_seen", "1");
                  }}
                  title="Contact Support"
                >
                  <Badge color="error" variant="dot" invisible={!showSupportHint}>
                    <HelpOutlineIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </AccountMenuContainerForBigScreen>
            <Box id="step-9"></Box>
            <AccountMenuContainerForBigScreen>
              <AccountMenu
                handleOpenUserMenu={handleOpenUserMenu}
                user={user}
                currentUser={currentUser}
                anchorElUser={anchorElUser}
                userMenu={userMenu}
                setAnchorElUser={setAnchorElUser}
                handleCloseUserMenu={handleCloseUserMenu}
              />
            </AccountMenuContainerForBigScreen>
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
};
